import React, { useState } from 'react';
import { Hero } from './components/Hero/Hero';
import { URLInput } from './components/URLInput/URLInput';
import { ProcessingStatus } from './components/ProcessingStatus/ProcessingStatus';
import { VideoPlayer } from './components/VideoPlayer/VideoPlayer';
import { TranscriptViewer } from './components/TranscriptViewer/TranscriptViewer';
import { useVideoProcessor } from './hooks/useVideoProcessor';

function App() {
  const { state, result, processVideo, cancel, retry } = useVideoProcessor();
  const [currentVideoTime, setCurrentVideoTime] = useState(0);

  const handleURLSubmit = (url: string) => {
    processVideo(url);
  };

  const handleSeek = (time: number) => {
    setCurrentVideoTime(time);
    // In a real implementation, this would control the video player
  };

  const renderContent = () => {
    if (state.error) {
      return (
        <ProcessingStatus
          currentStep={state.currentStep}
          totalSteps={4}
          progress={state.progress}
          estimatedTime={state.estimatedTime}
          error={state.error}
          onCancel={cancel}
          onRetry={retry}
        />
      );
    }

    if (state.isProcessing) {
      return (
        <ProcessingStatus
          currentStep={state.currentStep}
          totalSteps={4}
          progress={state.progress}
          estimatedTime={state.estimatedTime}
          onCancel={cancel}
        />
      );
    }

    if (result) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Video Player */}
            <div>
              <VideoPlayer
                videoUrl={result.videoUrl}
                originalDuration={result.metadata.originalDuration}
                summaryDuration={result.metadata.summaryDuration}
                compressionRatio={result.metadata.compressionRatio}
                metadata={result.metadata}
              />
            </div>

            {/* Transcript Viewer */}
            <div>
              <TranscriptViewer
                transcript={result.transcript}
                currentTime={currentVideoTime}
                onSeek={handleSeek}
              />
            </div>
          </div>

          {/* Try Another Video */}
          <div className="text-center mt-12">
            <button
              onClick={cancel}
              className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
            >
              Summarize Another Video
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        <Hero />
        <URLInput onSubmit={handleURLSubmit} isProcessing={state.isProcessing} />
        
        {/* Features Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose SummarizeAI?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the future of content consumption with our advanced AI technology 
              that transforms hours of video into minutes of actionable insights.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Lightning Fast</h3>
              <p className="text-gray-600 leading-relaxed">
                Process videos up to 10x faster than manual summarization while maintaining 
                accuracy and context relevance.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Smart Analysis</h3>
              <p className="text-gray-600 leading-relaxed">
                Advanced AI algorithms identify key concepts, main arguments, and actionable 
                insights from any video content.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center hover:shadow-2xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-coral-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Multiple Formats</h3>
              <p className="text-gray-600 leading-relaxed">
                Download summaries as video, audio, text, or PDF. Perfect for different 
                learning styles and use cases.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderContent()}
    </div>
  );
}

export default App;